import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Anchor, ArrowLeft, Plus, Trash2, Save, GripVertical, Settings } from "lucide-react";
import { useBillingStatus } from "@/components/PaywallBanner";
import { CURRENCIES } from "@/pages/Dashboard";
import SmsSettings from "@/components/SmsSettings";
import CoCaptainSection from "@/components/vessel-settings/CoCaptainSection";
import DangerZoneSection from "@/components/vessel-settings/DangerZoneSection";
import PublicProfileSection from "@/components/PublicProfileSection";
import ProGate from "@/components/ProGate";

// Categories that are special-cased in the Dashboard (reference catalogs).
// We allow customizing their custom fields but warn that the layout is fixed.
const RESERVED_CATEGORIES = new Set(["Parts", "Zincs"]);

// Suggested/established categories — same union the backend seeds across
// powerboat + sailboat presets. Used to power the Categories dropdown so
// users can re-add a missing tab (e.g. Parts) with one click instead of
// retyping it.
const SUGGESTED_CATEGORIES = [
    "Maintenance",
    "Zincs",
    "Parts",
    "Rigging",
    "Sails",
    "Costs",
    "Fuel",
    "Fuel Economy",
    "Cruising Log",
    "Receipts",
    "Surveys",
    "Insurance",
    "Title",
    "Haul-Out",
    "Inspections",
];

const FIELD_TYPES = [
    { value: "text", label: "Text" },
    { value: "number", label: "Number" },
    { value: "date", label: "Date" },
    { value: "select", label: "Dropdown" },
];

function newId() {
    return "f_" + Math.random().toString(36).slice(2, 9);
}

export default function VesselSettings() {
    const { vesselId } = useParams();
    const nav = useNavigate();
    const { user } = useAuth();
    const billing = useBillingStatus(vesselId ? 1 : 0);
    const canWrite = !billing || billing.is_active;

    const [vessel, setVessel] = useState(null);
    const [categories, setCategories] = useState([]);
    const [customFields, setCustomFields] = useState({});
    const [displayCcy, setDisplayCcy] = useState("USD");
    const [fxRates, setFxRates] = useState({});
    const [engines, setEngines] = useState([]);
    const [tanks, setTanks] = useState([]);
    const [newCat, setNewCat] = useState("");
    const [activeCat, setActiveCat] = useState(null);
    const [busy, setBusy] = useState(false);
    const [savedFlash, setSavedFlash] = useState(false);
    const [err, setErr] = useState("");

    useEffect(() => {
        if (!vesselId) return;
        api.get(`/vessels/${vesselId}`).then(({ data }) => {
            setVessel(data);
            setCategories(data.categories || []);
            setCustomFields(data.custom_fields || {});
            setDisplayCcy(data.display_currency || "USD");
            setFxRates(data.fx_rates || {});
            setEngines((data.engines || []).map((e) => ({ ...e })));
            setTanks((data.tanks || []).map((t) => ({ ...t })));
            const first = (data.categories || []).find((c) => !RESERVED_CATEGORIES.has(c)) || (data.categories || [])[0] || null;
            setActiveCat(first);
        }).catch(() => {
            nav("/app", { replace: true });
        });
    }, [vesselId, nav]);

    const dirty = useMemo(() => {
        if (!vessel) return false;
        const sameCats = JSON.stringify(vessel.categories || []) === JSON.stringify(categories);
        const sameFields = JSON.stringify(vessel.custom_fields || {}) === JSON.stringify(customFields);
        const sameCcy = (vessel.display_currency || "USD") === displayCcy;
        const sameFx = JSON.stringify(vessel.fx_rates || {}) === JSON.stringify(fxRates);
        const sameEngines = JSON.stringify(vessel.engines || []) === JSON.stringify(engines);
        const sameTanks = JSON.stringify(vessel.tanks || []) === JSON.stringify(tanks);
        return !(sameCats && sameFields && sameCcy && sameFx && sameEngines && sameTanks);
    }, [vessel, categories, customFields, displayCcy, fxRates, engines, tanks]);

    // FX rate rows: every currency except the display one. Memoised so the
    // .filter() doesn't re-run on every render of the unrelated bits of this
    // page (engine edits, category drag, etc.).
    const nonDisplayCurrencies = useMemo(
        () => CURRENCIES.filter((c) => c !== displayCcy),
        [displayCcy],
    );

    const addCategory = (raw) => {
        const name = (raw ?? newCat).trim();
        if (!name) return;
        if (categories.some((c) => c.toLowerCase() === name.toLowerCase())) {
            setErr(`"${name}" already exists.`);
            return;
        }
        setCategories([...categories, name]);
        setActiveCat(name);
        setNewCat("");
        setErr("");
    };

    const removeCategory = (name) => {
        if (!window.confirm(`Remove "${name}"? Existing entries are kept but the tab is hidden.`)) return;
        const next = categories.filter((c) => c !== name);
        setCategories(next);
        const cf = { ...customFields };
        delete cf[name];
        setCustomFields(cf);
        if (activeCat === name) setActiveCat(next[0] || null);
    };

    const renameCategory = (oldName, newName) => {
        newName = newName.trim();
        if (!newName || newName === oldName) return;
        if (categories.some((c) => c.toLowerCase() === newName.toLowerCase())) {
            setErr(`"${newName}" already exists.`);
            return;
        }
        setCategories(categories.map((c) => (c === oldName ? newName : c)));
        const cf = { ...customFields };
        if (cf[oldName]) {
            cf[newName] = cf[oldName];
            delete cf[oldName];
        }
        setCustomFields(cf);
        if (activeCat === oldName) setActiveCat(newName);
        setErr("");
    };

    const moveCategory = (idx, dir) => {
        const next = [...categories];
        const j = idx + dir;
        if (j < 0 || j >= next.length) return;
        [next[idx], next[j]] = [next[j], next[idx]];
        setCategories(next);
    };

    const getFields = (cat) => customFields[cat] || [];

    const addField = () => {
        if (!activeCat) return;
        const next = { ...customFields };
        next[activeCat] = [...(next[activeCat] || []), { key: newId(), label: "", type: "text", options: [] }];
        setCustomFields(next);
    };

    const updateField = (idx, patch) => {
        const next = { ...customFields };
        const list = [...(next[activeCat] || [])];
        list[idx] = { ...list[idx], ...patch };
        next[activeCat] = list;
        setCustomFields(next);
    };

    const removeField = (idx) => {
        const next = { ...customFields };
        const list = [...(next[activeCat] || [])];
        list.splice(idx, 1);
        next[activeCat] = list;
        setCustomFields(next);
    };

    const moveField = (idx, dir) => {
        const next = { ...customFields };
        const list = [...(next[activeCat] || [])];
        const j = idx + dir;
        if (j < 0 || j >= list.length) return;
        [list[idx], list[j]] = [list[j], list[idx]];
        next[activeCat] = list;
        setCustomFields(next);
    };

    // ---- Engines (multi-engine support, max 6) ----
    const ENGINE_LABEL_DEFAULTS = {
        1: ["Main"],
        2: ["Port", "Starboard"],
        3: ["Port", "Center", "Starboard"],
        4: ["Port outer", "Port inner", "Stbd inner", "Stbd outer"],
        5: ["Engine 1", "Engine 2", "Engine 3", "Engine 4", "Engine 5"],
        6: ["Engine 1", "Engine 2", "Engine 3", "Engine 4", "Engine 5", "Engine 6"],
    };
    const addEngine = () => {
        if (engines.length >= 6) return;
        const nextCount = engines.length + 1;
        const defaults = ENGINE_LABEL_DEFAULTS[nextCount] || [];
        const next = [];
        for (let i = 0; i < nextCount; i++) {
            const existing = engines[i];
            if (existing) {
                next.push(existing);
            } else {
                next.push({ label: defaults[i] || `Engine ${i + 1}`, make: "", model: "", hours_current: 0, fuel_type: "diesel" });
            }
        }
        // If we are growing from a single "Main" engine, rename it to fit the new layout
        if (engines.length === 1 && next.length === 2 && (engines[0].label || "").toLowerCase() === "main") {
            next[0] = { ...next[0], label: defaults[0] };
        }
        setEngines(next);
    };
    const updateEngine = (idx, patch) => {
        const next = engines.map((e, i) => (i === idx ? { ...e, ...patch } : e));
        setEngines(next);
    };
    const removeEngine = (idx) => {
        if (engines.length <= 1) return; // always at least one engine
        const next = engines.filter((_, i) => i !== idx);
        setEngines(next);
    };

    // ---- Tanks (multi-tank support, max 6) ----
    const TANK_LABEL_DEFAULTS = {
        1: ["Main"],
        2: ["Port", "Starboard"],
        3: ["Port", "Center", "Starboard"],
        4: ["Port", "Center", "Starboard", "Aux"],
        5: ["Tank 1", "Tank 2", "Tank 3", "Tank 4", "Tank 5"],
        6: ["Tank 1", "Tank 2", "Tank 3", "Tank 4", "Tank 5", "Tank 6"],
    };
    const addTank = () => {
        if (tanks.length >= 6) return;
        const nextCount = tanks.length + 1;
        const defaults = TANK_LABEL_DEFAULTS[nextCount] || [];
        const next = [];
        for (let i = 0; i < nextCount; i++) {
            const existing = tanks[i];
            if (existing) next.push(existing);
            else next.push({ label: defaults[i] || `Tank ${i + 1}`, capacity: "", capacity_unit: "gal_us", fuel_type: "diesel" });
        }
        if (tanks.length === 1 && next.length === 2 && (tanks[0].label || "").toLowerCase() === "main") {
            next[0] = { ...next[0], label: defaults[0] };
        }
        setTanks(next);
    };
    const updateTank = (idx, patch) => {
        setTanks(tanks.map((t, i) => (i === idx ? { ...t, ...patch } : t)));
    };
    const removeTank = (idx) => {
        setTanks(tanks.filter((_, i) => i !== idx));
    };

    const save = async () => {
        setErr(""); setBusy(true);
        try {
            // Strip empty-labeled fields, default empty options, drop orphans (category removed)
            const cleaned = {};
            const validCats = new Set(categories);
            for (const [cat, fields] of Object.entries(customFields)) {
                if (!validCats.has(cat)) continue;
                const list = (fields || [])
                    .filter((f) => (f.label || "").trim())
                    .map((f) => ({
                        key: f.key || newId(),
                        label: f.label.trim(),
                        type: f.type || "text",
                        options: f.type === "select" ? (f.options || []).filter((o) => (o || "").trim()) : [],
                    }));
                if (list.length) cleaned[cat] = list;
            }
            const { data } = await api.patch(`/vessels/${vesselId}`, {
                categories,
                custom_fields: cleaned,
                display_currency: displayCcy,
                fx_rates: Object.fromEntries(
                    Object.entries(fxRates)
                        .map(([k, v]) => [k.toUpperCase(), parseFloat(v)])
                        .filter(([k, v]) => k && Number.isFinite(v) && v > 0 && k !== displayCcy.toUpperCase())
                ),
                engines: engines.map((e) => ({
                    id: e.id || undefined,
                    label: (e.label || "").trim() || "Engine",
                    make: (e.make || "").trim() || null,
                    model: (e.model || "").trim() || null,
                    serial: (e.serial || "").trim() || null,
                    year: (e.year || "").toString().trim() || null,
                    hp_kw: (e.hp_kw || "").toString().trim() || null,
                    hours_current: parseFloat(e.hours_current) || 0,
                    fuel_type: e.fuel_type || null,
                    notes: (e.notes || "").trim() || null,
                })),
                tanks: tanks.map((t) => ({
                    id: t.id || undefined,
                    label: (t.label || "").trim() || "Tank",
                    capacity: t.capacity === "" || t.capacity == null ? null : parseFloat(t.capacity),
                    capacity_unit: t.capacity_unit || "gal_us",
                    fuel_type: t.fuel_type || null,
                    notes: (t.notes || "").trim() || null,
                })),
            });
            setVessel(data);
            setCategories(data.categories || []);
            setCustomFields(data.custom_fields || {});
            setDisplayCcy(data.display_currency || "USD");
            setFxRates(data.fx_rates || {});
            setEngines((data.engines || []).map((e) => ({ ...e })));
            setTanks((data.tanks || []).map((t) => ({ ...t })));
            setSavedFlash(true);
            setTimeout(() => setSavedFlash(false), 2000);
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };

    if (!vessel) return null;

    return (
        <div data-testid="vessel-settings-page">
            {/* Header */}
            <div style={{ borderBottom: "1px solid var(--bm-border-soft)", background: "var(--bm-bg-1)" }}>
                <div style={{ maxWidth: 1100, margin: "0 auto", padding: "18px 24px", display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
                    <button data-testid="back-btn" className="bm-btn bm-btn-ghost" onClick={() => nav(`/app/${vesselId}`)} style={{ padding: "8px 12px" }}>
                        <ArrowLeft size={15} /> Back
                    </button>
                    <Anchor size={18} color="var(--bm-gold)" />
                    <div className="bm-display" style={{ fontSize: 20 }}>Bosun's Mate</div>
                    <span style={{ width: 1, height: 18, background: "var(--bm-border)" }} />
                    <Settings size={16} color="var(--bm-text-muted)" />
                    <span style={{ fontSize: 14, color: "var(--bm-text-muted)" }}>Settings · {vessel.name}</span>
                    <div style={{ flex: 1 }} />
                    <button
                        data-testid="settings-save-btn"
                        className="bm-btn bm-btn-gold"
                        onClick={save}
                        disabled={busy || !dirty || !canWrite}
                        style={{ padding: "8px 14px", opacity: (busy || !dirty || !canWrite) ? 0.5 : 1 }}
                    >
                        <Save size={15} /> {busy ? "Saving…" : savedFlash ? "Saved ✓" : "Save changes"}
                    </button>
                </div>
            </div>

            <main style={{ maxWidth: 1100, margin: "0 auto", padding: "28px clamp(16px, 4vw, 32px)" }}>
                {err && <div data-testid="settings-err" style={{ color: "var(--bm-bad)", marginBottom: 14, fontSize: 14 }}>{err}</div>}
                {!canWrite && (
                    <div className="bm-card" style={{ padding: 14, marginBottom: 16, color: "var(--bm-warn)", fontSize: 14 }}>
                        Read-only mode — subscribe to edit categories and custom fields.
                    </div>
                )}

                {/* Engines */}
                <section className="bm-card" data-testid="engines-section" style={{ padding: 24, marginBottom: 22 }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                        <h3 className="bm-display" style={{ margin: 0, fontSize: 18, color: "var(--bm-gold)" }}>Engines</h3>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <span style={{ fontSize: 12, color: "var(--bm-text-muted)" }}>{engines.length} / 6</span>
                            <button
                                type="button"
                                data-testid="add-engine-btn"
                                className="bm-btn bm-btn-ghost"
                                onClick={addEngine}
                                disabled={!canWrite || engines.length >= 6}
                                style={{ padding: "6px 12px", fontSize: 13 }}
                            >
                                <Plus size={14} /> Add engine
                            </button>
                        </div>
                    </div>
                    <p style={{ color: "var(--bm-text-muted)", fontSize: 13, margin: "0 0 14px", lineHeight: 1.5 }}>
                        Each engine tracks its own running hours. Twins, triples, and quad outboards are all supported. Maintenance, fuel and fuel-economy entries can be tagged to a specific engine.
                    </p>
                    <div style={{ display: "grid", gap: 12 }}>
                        {engines.map((e, idx) => (
                            <div
                                key={e.id || `new-${idx}`}
                                data-testid={`engine-row-${idx}`}
                                style={{
                                    border: "1px solid var(--bm-border-soft)",
                                    borderRadius: "var(--bm-r-md)",
                                    padding: "12px 14px",
                                    background: "var(--bm-bg-1)",
                                }}
                            >
                                <div className="bm-engine-row" style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr 1fr 1fr 0.9fr 36px", gap: 10, alignItems: "end" }}>
                                    <div>
                                        <label className="bm-label" style={{ fontSize: 11 }}>Label</label>
                                        <input
                                            data-testid={`engine-label-${idx}`}
                                            className="bm-input"
                                            value={e.label || ""}
                                            placeholder="Port"
                                            onChange={(ev) => updateEngine(idx, { label: ev.target.value })}
                                            disabled={!canWrite}
                                        />
                                    </div>
                                    <div>
                                        <label className="bm-label" style={{ fontSize: 11 }}>Make</label>
                                        <input
                                            data-testid={`engine-make-${idx}`}
                                            className="bm-input"
                                            value={e.make || ""}
                                            placeholder="Volvo"
                                            onChange={(ev) => updateEngine(idx, { make: ev.target.value })}
                                            disabled={!canWrite}
                                        />
                                    </div>
                                    <div>
                                        <label className="bm-label" style={{ fontSize: 11 }}>Model</label>
                                        <input
                                            data-testid={`engine-model-${idx}`}
                                            className="bm-input"
                                            value={e.model || ""}
                                            placeholder="D4-260"
                                            onChange={(ev) => updateEngine(idx, { model: ev.target.value })}
                                            disabled={!canWrite}
                                        />
                                    </div>
                                    <div>
                                        <label className="bm-label" style={{ fontSize: 11 }}>Hours</label>
                                        <input
                                            data-testid={`engine-hours-${idx}`}
                                            className="bm-input"
                                            type="number"
                                            inputMode="decimal"
                                            step="0.1"
                                            value={e.hours_current ?? 0}
                                            onChange={(ev) => updateEngine(idx, { hours_current: ev.target.value })}
                                            disabled={!canWrite}
                                        />
                                    </div>
                                    <div>
                                        <label className="bm-label" style={{ fontSize: 11 }}>Fuel</label>
                                        <select
                                            data-testid={`engine-fuel-${idx}`}
                                            className="bm-input"
                                            value={e.fuel_type || "diesel"}
                                            onChange={(ev) => updateEngine(idx, { fuel_type: ev.target.value })}
                                            disabled={!canWrite}
                                        >
                                            <option value="diesel">Diesel</option>
                                            <option value="gasoline">Gasoline</option>
                                            <option value="electric">Electric</option>
                                            <option value="mixed">Mixed</option>
                                        </select>
                                    </div>
                                    <button
                                        type="button"
                                        data-testid={`engine-remove-${idx}`}
                                        className="bm-btn bm-btn-ghost"
                                        onClick={() => removeEngine(idx)}
                                        disabled={!canWrite || engines.length <= 1}
                                        title={engines.length <= 1 ? "At least one engine required" : "Remove engine"}
                                        style={{ padding: "6px 8px" }}
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Tanks */}
                <section className="bm-card" data-testid="tanks-section" style={{ padding: 24, marginBottom: 22 }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                        <h3 className="bm-display" style={{ margin: 0, fontSize: 18, color: "var(--bm-gold)" }}>Fuel Tanks</h3>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <span style={{ fontSize: 12, color: "var(--bm-text-muted)" }}>{tanks.length} / 6</span>
                            <button
                                type="button"
                                data-testid="add-tank-btn"
                                className="bm-btn bm-btn-ghost"
                                onClick={addTank}
                                disabled={!canWrite || tanks.length >= 6}
                                style={{ padding: "6px 12px", fontSize: 13 }}
                            >
                                <Plus size={14} /> Add tank
                            </button>
                        </div>
                    </div>
                    <p style={{ color: "var(--bm-text-muted)", fontSize: 13, margin: "0 0 14px", lineHeight: 1.5 }}>
                        Track each fuel tank's capacity and fuel type. Total fuel capacity will appear on the Vessel Info tab.
                    </p>
                    {tanks.length === 0 ? (
                        <div style={{ padding: 18, border: "1px dashed var(--bm-border-soft)", borderRadius: "var(--bm-r-md)", textAlign: "center", color: "var(--bm-text-muted)", fontSize: 13 }}>
                            No tanks configured — tap <strong style={{ color: "var(--bm-text)" }}>Add tank</strong> to start.
                        </div>
                    ) : (
                        <div style={{ display: "grid", gap: 12 }}>
                            {tanks.map((t, idx) => (
                                <div
                                    key={t.id || `new-tank-${idx}`}
                                    data-testid={`tank-row-${idx}`}
                                    style={{
                                        border: "1px solid var(--bm-border-soft)",
                                        borderRadius: "var(--bm-r-md)",
                                        padding: "12px 14px",
                                        background: "var(--bm-bg-1)",
                                    }}
                                >
                                    <div className="bm-tank-row" style={{ display: "grid", gridTemplateColumns: "1.2fr 0.8fr 0.8fr 1fr 36px", gap: 10, alignItems: "end" }}>
                                        <div>
                                            <label className="bm-label" style={{ fontSize: 11 }}>Label</label>
                                            <input
                                                data-testid={`tank-label-${idx}`}
                                                className="bm-input"
                                                value={t.label || ""}
                                                placeholder="Port"
                                                onChange={(ev) => updateTank(idx, { label: ev.target.value })}
                                                disabled={!canWrite}
                                            />
                                        </div>
                                        <div>
                                            <label className="bm-label" style={{ fontSize: 11 }}>Capacity</label>
                                            <input
                                                data-testid={`tank-capacity-${idx}`}
                                                className="bm-input"
                                                type="number"
                                                step="0.1"
                                                value={t.capacity ?? ""}
                                                placeholder="100"
                                                onChange={(ev) => updateTank(idx, { capacity: ev.target.value })}
                                                disabled={!canWrite}
                                            />
                                        </div>
                                        <div>
                                            <label className="bm-label" style={{ fontSize: 11 }}>Unit</label>
                                            <select
                                                data-testid={`tank-unit-${idx}`}
                                                className="bm-input"
                                                value={t.capacity_unit || "gal_us"}
                                                onChange={(ev) => updateTank(idx, { capacity_unit: ev.target.value })}
                                                disabled={!canWrite}
                                            >
                                                <option value="gal_us">US gal</option>
                                                <option value="gal_imp">Imp gal</option>
                                                <option value="L">Litres</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="bm-label" style={{ fontSize: 11 }}>Fuel type</label>
                                            <select
                                                data-testid={`tank-fuel-${idx}`}
                                                className="bm-input"
                                                value={t.fuel_type || "diesel"}
                                                onChange={(ev) => updateTank(idx, { fuel_type: ev.target.value })}
                                                disabled={!canWrite}
                                            >
                                                <option value="diesel">Diesel</option>
                                                <option value="gasoline">Gasoline</option>
                                                <option value="mixed">Mixed</option>
                                            </select>
                                        </div>
                                        <button
                                            type="button"
                                            data-testid={`tank-remove-${idx}`}
                                            className="bm-btn bm-btn-ghost"
                                            onClick={() => removeTank(idx)}
                                            disabled={!canWrite}
                                            title="Remove this tank"
                                            style={{ padding: "8px", color: "var(--bm-bad)" }}
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </section>


                {/* SMS notifications (user-level) — Pro feature */}
                <ProGate
                    feature="sms"
                    title="SMS maintenance alerts"
                    description="Get a text when service comes due — even when you haven't opened the app in months. Part of Bosun's Mate Pro."
                >
                    <SmsSettings canWrite={canWrite} />
                </ProGate>

                {/* Public profile — opt-in network discoverability */}
                {canWrite && <PublicProfileSection vesselId={vesselId} />}

                {/* Currency settings */}
                <section data-testid="currency-section" className="bm-card" style={{ padding: 22, marginBottom: 22 }}>
                    <h2 className="bm-serif" style={{ fontSize: 22, margin: 0, fontWeight: 500 }}>Currency</h2>
                    <p style={{ color: "var(--bm-text-muted)", margin: "4px 0 16px", fontSize: 13 }}>
                        Each cost entry has its own currency. Set your display currency below and provide conversion rates so YTD totals roll up correctly.
                    </p>
                    <div style={{ display: "grid", gridTemplateColumns: "minmax(200px, 280px) 1fr", gap: 18 }} className="bm-currency-grid">
                        <div>
                            <label className="bm-label">Display currency (for totals)</label>
                            <select
                                data-testid="display-currency-select"
                                className="bm-input"
                                value={displayCcy}
                                disabled={!canWrite}
                                onChange={(e) => setDisplayCcy(e.target.value)}
                            >
                                {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                            </select>
                            <div style={{ fontSize: 12, color: "var(--bm-text-muted)", marginTop: 6 }}>
                                YTD costs & the cost-breakdown panel are shown in {displayCcy}.
                            </div>
                        </div>
                        <div>
                            <label className="bm-label">Conversion rates &nbsp;<span style={{ color: "var(--bm-text-muted)", fontWeight: 400 }}>(1 unit of source = X {displayCcy})</span></label>
                            <div data-testid="fx-rates-list" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 10 }}>
                                {nonDisplayCurrencies.map((c) => (
                                    <div key={c} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                        <span style={{ width: 46, color: "var(--bm-text-muted)", fontSize: 13, fontVariantNumeric: "tabular-nums" }}>1 {c} =</span>
                                        <input
                                            data-testid={`fx-rate-${c}`}
                                            className="bm-input"
                                            type="number"
                                            step="0.0001"
                                            min="0"
                                            disabled={!canWrite}
                                            value={fxRates[c] ?? ""}
                                            placeholder="e.g. 0.74"
                                            onChange={(e) => setFxRates({ ...fxRates, [c]: e.target.value })}
                                            style={{ flex: 1, padding: "6px 8px" }}
                                        />
                                        <span style={{ color: "var(--bm-text-muted)", fontSize: 12 }}>{displayCcy}</span>
                                    </div>
                                ))}
                            </div>
                            <div style={{ fontSize: 12, color: "var(--bm-text-muted)", marginTop: 8 }}>
                                Tip: leave a rate blank to skip — entries in that currency will be summed as-is (assumed equal to {displayCcy}).
                            </div>
                        </div>
                    </div>
                </section>

                {/* Co-Captain management — Pro feature */}
                <ProGate
                    feature="co-captains"
                    title="Co-Captains"
                    description="Share your vessel with your partner, mechanic or crew — viewer, editor or admin roles, each with their own free login. Part of Bosun's Mate Pro."
                >
                    <CoCaptainSection
                        vesselId={vesselId}
                        viewerRole={vessel.viewer_role || "owner"}
                        currentUserId={user?.id}
                    />
                </ProGate>

                <div style={{ display: "grid", gridTemplateColumns: "minmax(260px, 320px) 1fr", gap: 22 }} className="bm-settings-grid">
                    {/* Categories */}
                    <section className="bm-card" style={{ padding: 22 }}>
                        <h2 className="bm-serif" style={{ fontSize: 22, margin: 0, fontWeight: 500 }}>Categories</h2>
                        <p style={{ color: "var(--bm-text-muted)", margin: "4px 0 14px", fontSize: 13 }}>
                            The tabs that appear on your dashboard.
                        </p>

                        <div data-testid="categories-list" style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 14 }}>
                            {categories.map((c, i) => (
                                <div
                                    key={c}
                                    data-testid={`category-row-${i}`}
                                    onClick={() => setActiveCat(c)}
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 6,
                                        padding: "6px 8px",
                                        borderRadius: 6,
                                        background: activeCat === c ? "rgba(212,181,114,0.10)" : "transparent",
                                        border: activeCat === c ? "1px solid var(--bm-gold-dim)" : "1px solid transparent",
                                        cursor: "pointer",
                                    }}
                                >
                                    <GripVertical size={14} color="var(--bm-text-dim)" />
                                    <input
                                        data-testid={`category-name-${i}`}
                                        className="bm-input"
                                        value={c}
                                        disabled={!canWrite}
                                        onClick={(e) => e.stopPropagation()}
                                        onChange={(e) => {
                                            const v = e.target.value;
                                            const next = [...categories];
                                            next[i] = v;
                                            setCategories(next);
                                        }}
                                        onBlur={(e) => renameCategory(c, e.target.value)}
                                        style={{ padding: "6px 8px", fontSize: 13, background: "transparent", border: "none" }}
                                    />
                                    <button
                                        type="button"
                                        data-testid={`category-up-${i}`}
                                        onClick={(e) => { e.stopPropagation(); moveCategory(i, -1); }}
                                        disabled={i === 0 || !canWrite}
                                        className="bm-btn bm-btn-ghost"
                                        style={{ padding: "2px 6px", fontSize: 11, opacity: i === 0 ? 0.4 : 1 }}
                                        title="Move up"
                                    >↑</button>
                                    <button
                                        type="button"
                                        data-testid={`category-down-${i}`}
                                        onClick={(e) => { e.stopPropagation(); moveCategory(i, 1); }}
                                        disabled={i === categories.length - 1 || !canWrite}
                                        className="bm-btn bm-btn-ghost"
                                        style={{ padding: "2px 6px", fontSize: 11, opacity: i === categories.length - 1 ? 0.4 : 1 }}
                                        title="Move down"
                                    >↓</button>
                                    <button
                                        type="button"
                                        data-testid={`category-remove-${i}`}
                                        onClick={(e) => { e.stopPropagation(); removeCategory(c); }}
                                        disabled={!canWrite}
                                        className="bm-btn bm-btn-danger"
                                        style={{ padding: "4px 8px" }}
                                        title="Remove"
                                    >
                                        <Trash2 size={12} />
                                    </button>
                                </div>
                            ))}
                            {categories.length === 0 && (
                                <div style={{ color: "var(--bm-text-muted)", fontSize: 13, padding: "6px 0" }}>No categories yet — add one below.</div>
                            )}
                        </div>

                        {/* Quick-add: dropdown of suggested categories not yet on this vessel */}
                        {(() => {
                            const lowerExisting = new Set(categories.map((c) => c.toLowerCase()));
                            const missing = SUGGESTED_CATEGORIES.filter((s) => !lowerExisting.has(s.toLowerCase()));
                            if (missing.length === 0) return null;
                            return (
                                <div data-testid="quick-add-categories" style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
                                    <span style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--bm-text-dim)", alignSelf: "center", marginRight: 4 }}>
                                        Quick add
                                    </span>
                                    {missing.map((s) => (
                                        <button
                                            key={s}
                                            type="button"
                                            data-testid={`quick-add-${s.toLowerCase().replace(/\s+/g, "-")}`}
                                            className="bm-btn bm-btn-ghost"
                                            disabled={!canWrite}
                                            onClick={() => addCategory(s)}
                                            style={{ padding: "4px 10px", fontSize: 12, borderRadius: 999 }}
                                        >
                                            <Plus size={11} /> {s}
                                        </button>
                                    ))}
                                </div>
                            );
                        })()}

                        <div style={{ display: "flex", gap: 6 }}>
                            <input
                                data-testid="new-category-input"
                                className="bm-input"
                                placeholder="Custom category name (or use Quick add above)"
                                value={newCat}
                                disabled={!canWrite}
                                onChange={(e) => setNewCat(e.target.value)}
                                onKeyDown={(e) => { if (e.key === "Enter") addCategory(); }}
                                style={{ flex: 1 }}
                            />
                            <button
                                type="button"
                                data-testid="add-category-btn"
                                className="bm-btn bm-btn-primary"
                                onClick={() => addCategory()}
                                disabled={!canWrite || !newCat.trim()}
                            >
                                <Plus size={14} /> Add
                            </button>
                        </div>
                    </section>

                    {/* Custom fields editor — Pro feature */}
                    <ProGate
                        feature="custom-fields"
                        title="Custom fields"
                        description="Define your own per-category fields — hours run, technician, part grade, anything your boat needs. Part of Bosun's Mate Pro."
                    >
                    <section className="bm-card" style={{ padding: 22 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
                            <div>
                                <h2 className="bm-serif" style={{ fontSize: 22, margin: 0, fontWeight: 500 }}>
                                    Custom fields {activeCat ? <span style={{ color: "var(--bm-gold)" }}>· {activeCat}</span> : null}
                                </h2>
                                <p style={{ color: "var(--bm-text-muted)", margin: "4px 0 0", fontSize: 13 }}>
                                    Extra fields that appear when you add an entry to this category.
                                    {activeCat && RESERVED_CATEGORIES.has(activeCat) && (
                                        <span style={{ color: "var(--bm-warn)" }}>
                                            {" "}· {activeCat} uses the parts-catalog layout; custom fields will be stored but not shown in the table.
                                        </span>
                                    )}
                                </p>
                            </div>
                            <button
                                type="button"
                                data-testid="add-field-btn"
                                className="bm-btn bm-btn-primary"
                                onClick={addField}
                                disabled={!activeCat || !canWrite}
                            >
                                <Plus size={14} /> Add field
                            </button>
                        </div>

                        {!activeCat && (
                            <div style={{ color: "var(--bm-text-muted)", fontSize: 14, padding: "20px 0" }}>
                                Pick a category on the left to add custom fields.
                            </div>
                        )}

                        {activeCat && (
                            <div data-testid="fields-list" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                                {getFields(activeCat).length === 0 && (
                                    <div style={{ color: "var(--bm-text-muted)", fontSize: 13, padding: "10px 0" }}>
                                        No custom fields yet — your entries will use the standard layout.
                                    </div>
                                )}
                                {getFields(activeCat).map((f, i) => (
                                    <div
                                        key={f.key}
                                        data-testid={`field-row-${i}`}
                                        style={{
                                            display: "grid",
                                            gridTemplateColumns: "1fr 140px auto",
                                            gap: 10,
                                            alignItems: "flex-start",
                                            padding: 12,
                                            background: "rgba(8,22,40,0.5)",
                                            border: "1px solid var(--bm-border-soft)",
                                            borderRadius: "var(--bm-r)",
                                        }}
                                    >
                                        <div>
                                            <label className="bm-label">Label</label>
                                            <input
                                                data-testid={`field-label-${i}`}
                                                className="bm-input"
                                                value={f.label}
                                                disabled={!canWrite}
                                                onChange={(e) => updateField(i, { label: e.target.value })}
                                                placeholder="e.g. Engine hours, PSI, Service shop"
                                            />
                                            {f.type === "select" && (
                                                <div style={{ marginTop: 8 }}>
                                                    <label className="bm-label">Dropdown options (comma-separated)</label>
                                                    <input
                                                        data-testid={`field-options-${i}`}
                                                        className="bm-input"
                                                        value={(f.options || []).join(", ")}
                                                        disabled={!canWrite}
                                                        onChange={(e) => updateField(i, { options: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                                                        placeholder="e.g. Diesel, Gas, Electric"
                                                    />
                                                </div>
                                            )}
                                        </div>
                                        <div>
                                            <label className="bm-label">Type</label>
                                            <select
                                                data-testid={`field-type-${i}`}
                                                className="bm-input"
                                                value={f.type}
                                                disabled={!canWrite}
                                                onChange={(e) => updateField(i, { type: e.target.value, options: e.target.value === "select" ? (f.options || []) : [] })}
                                            >
                                                {FIELD_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                                            </select>
                                        </div>
                                        <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 22 }}>
                                            <button
                                                type="button"
                                                data-testid={`field-up-${i}`}
                                                onClick={() => moveField(i, -1)}
                                                disabled={i === 0 || !canWrite}
                                                className="bm-btn bm-btn-ghost"
                                                style={{ padding: "4px 8px", fontSize: 11, opacity: i === 0 ? 0.4 : 1 }}
                                            >↑</button>
                                            <button
                                                type="button"
                                                data-testid={`field-down-${i}`}
                                                onClick={() => moveField(i, 1)}
                                                disabled={i === getFields(activeCat).length - 1 || !canWrite}
                                                className="bm-btn bm-btn-ghost"
                                                style={{ padding: "4px 8px", fontSize: 11, opacity: i === getFields(activeCat).length - 1 ? 0.4 : 1 }}
                                            >↓</button>
                                            <button
                                                type="button"
                                                data-testid={`field-remove-${i}`}
                                                onClick={() => removeField(i)}
                                                disabled={!canWrite}
                                                className="bm-btn bm-btn-danger"
                                                style={{ padding: "4px 8px" }}
                                            >
                                                <Trash2 size={12} />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </section>
                    </ProGate>
                </div>

                {/* Sticky save bar */}
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 22 }}>
                    <button
                        data-testid="settings-save-bottom-btn"
                        className="bm-btn bm-btn-gold"
                        onClick={save}
                        disabled={busy || !dirty || !canWrite}
                        style={{ padding: "12px 22px", opacity: (busy || !dirty || !canWrite) ? 0.5 : 1 }}
                    >
                        <Save size={15} /> {busy ? "Saving…" : savedFlash ? "Saved ✓" : "Save changes"}
                    </button>
                </div>

                <DangerZoneSection vessel={vessel} isOwner={!!(vessel && user && vessel.user_id === user.id)} />
            </main>

            <style>{`
                @media (max-width: 720px) {
                    .bm-settings-grid { grid-template-columns: 1fr !important; }
                    .bm-currency-grid { grid-template-columns: 1fr !important; }
                }
            `}</style>
        </div>
    );
}

